import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
} from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import CheckIcon from '@mui/icons-material/Check'
import WarningIcon from '@mui/icons-material/Warning'
import { collection, doc, orderBy, query } from 'firebase/firestore'
import { firebase_firestore } from '../../common/firebaseConfig'
import { useCollectionData, useDocument } from 'react-firebase-hooks/firestore'

interface VersionType {
  version: number
  created: number
  diff: string
  starred?: boolean
  note?: string
  hidden?: boolean
}

interface PatternVersionSelectorProps {
  patternId?: string
  gcid?: string
  tradingHouse?: string
  currentVersion?: number
  selectedVersion?: number | null
  onVersionSelect: (version: number | null) => void
  onLatestVersionResolved?: (version: number) => void
  onLoadingChange?: (loading: boolean) => void
  disabled?: boolean
}

export const PatternVersionSelector: React.FC<PatternVersionSelectorProps> = React.memo(
  ({
    patternId,
    gcid,
    tradingHouse,
    currentVersion,
    selectedVersion,
    onVersionSelect,
    onLatestVersionResolved,
    onLoadingChange,
    disabled = false,
  }) => {
    const [isExpanded, setIsExpanded] = useState(false)

    // Check if pattern exists in user's patterns
    const userDocPath = useMemo(() => {
      if (!patternId || !gcid) return null
      return `usermeta/${gcid}/patterns/${patternId}`
    }, [patternId, gcid])

    const [userPatternDoc, userPatternLoading] = useDocument(
      userDocPath ? doc(firebase_firestore, userDocPath) : null
    )

    // Determine the correct collection path based on whether user owns the pattern
    const versionsCollectionPath = useMemo(() => {
      if (!patternId) return null

      if (userPatternDoc?.exists()) {
        return `${userDocPath}/versions`
      } else if (tradingHouse) {
        return `tradingHouses/${tradingHouse}/standardPatterns/${patternId}/versions`
      }

      return null
    }, [patternId, userDocPath, userPatternDoc, tradingHouse])

    // Query the versions using the hook
    const [versionsData, versionsLoading] = useCollectionData(
      versionsCollectionPath
        ? query(collection(firebase_firestore, versionsCollectionPath), orderBy('version'))
        : null
    )

    // Convert the raw data to our version type
    const versions = useMemo(() => {
      return (versionsData as VersionType[]) || []
    }, [versionsData])

    // Get the latest version number
    const latestVersionNumber = useMemo(() => {
      if (!versions.length) return currentVersion || 1
      return Math.max(...versions.map((v) => v.version))
    }, [versions, currentVersion])

    // Let the parent know the pattern's true latest version, so it can request
    // a resync to latest (e.g. from an "auto-update" toggle) using the same
    // usePattern flow as manual version selection.
    useEffect(() => {
      onLatestVersionResolved?.(latestVersionNumber)
    }, [latestVersionNumber, onLatestVersionResolved])

    // Get versions in reverse order (newest first)
    const versionsReversed = useMemo(() => {
      return [...versions].reverse()
    }, [versions])

    // Determine which version is being used
    const activeVersion = selectedVersion ?? currentVersion ?? 1
    const isUsingLatest = activeVersion === latestVersionNumber

    // Loading state
    const isLoading = userPatternLoading || versionsLoading

    // Let the parent know when version history is still loading, so it can
    // avoid acting on a not-yet-resolved "latest version" (e.g. disable an
    // auto-update toggle until the real latest version number is known).
    useEffect(() => {
      onLoadingChange?.(isLoading)
    }, [isLoading, onLoadingChange])

    // Check if only one version exists
    const hasOnlyOneVersion = versions.length === 1 || (!versions.length && currentVersion === 1)

    // Check if versions failed to load
    const hasError =
      !versionsLoading &&
      versionsCollectionPath &&
      !versions.length &&
      currentVersion &&
      currentVersion > 1

    // Determine message text
    const getMessageText = () => {
      if (isLoading) {
        return 'Loading versions...'
      }
      if (hasError) {
        return `Using version ${activeVersion}`
      }
      if (hasOnlyOneVersion) {
        return 'Using version 1 (only version)'
      }
      if (isUsingLatest) {
        return `Using version ${activeVersion} (latest)`
      }
      return `Using version ${activeVersion}`
    }

    // Determine if clickable
    const isClickable = !disabled && !isLoading && !hasOnlyOneVersion && versions.length > 0

    // Handle click on message
    const handleClick = () => {
      if (isClickable) {
        setIsExpanded(!isExpanded)
      }
    }

    // Handle version selection
    const handleVersionSelect = (version: number) => {
      onVersionSelect(version)
      setIsExpanded(false)
    }

    // Format date
    const formatDate = (timestamp: number) => {
      const date = new Date(timestamp)
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }

    // Don't render if no pattern selected
    if (!patternId) {
      return null
    }

    // Don't render for showcase patterns (not owned by user and not a standard pattern)
    // This happens when versionsCollectionPath is null after checking both user and standard patterns
    if (!userPatternLoading && versionsCollectionPath === null) {
      return null
    }

    return (
      <Box sx={{ mt: 1, mb: 1 }}>
        {/* Collapsed message */}
        <Box
          onClick={handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.5,
            cursor: isClickable ? 'pointer' : 'default',
            color: isClickable ? 'text.secondary' : 'text.disabled',
            borderRadius: 1,
            '&:hover': isClickable
              ? {
                  color: 'primary.main',
                  bgcolor: 'action.hover',
                }
              : {},
          }}
        >
          {isLoading ? (
            <CircularProgress size={16} sx={{ color: 'text.secondary' }} />
          ) : hasError ? (
            <WarningIcon sx={{ fontSize: 16 }} />
          ) : (
            <HistoryIcon sx={{ fontSize: 16 }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {getMessageText()}
          </Typography>
        </Box>

        {/* Expanded version list */}
        <Collapse in={isExpanded}>
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Select Pattern Version
            </Typography>
            {hasError ? (
              <Typography variant="body2" color="error">
                Unable to load version history
              </Typography>
            ) : (
              <List dense disablePadding>
                {versionsReversed.map((version) => {
                  const isSelected = activeVersion === version.version
                  const isLatest = version.version === latestVersionNumber
                  const versionLabel = isLatest
                    ? `Version ${version.version} (latest)`
                    : `Version ${version.version}`

                  return (
                    <ListItem
                      key={version.version}
                      disablePadding
                      secondaryAction={
                        isSelected ? (
                          <CheckIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        ) : null
                      }
                    >
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => handleVersionSelect(version.version)}
                        sx={{
                          borderRadius: 1,
                          '&.Mui-selected': {
                            bgcolor: 'action.selected',
                          },
                        }}
                      >
                        <ListItemText
                          primary={versionLabel}
                          secondary={
                            <>
                              {formatDate(version.created)}
                              {version.note && (
                                <>
                                  {' - '}
                                  {version.note.length > 50
                                    ? version.note.substring(0, 50) + '...'
                                    : version.note}
                                </>
                              )}
                            </>
                          }
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontWeight: isLatest ? 600 : 400,
                          }}
                          secondaryTypographyProps={{
                            variant: 'caption',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
                })}
              </List>
            )}
          </Box>
        </Collapse>
      </Box>
    )
  }
)

PatternVersionSelector.displayName = 'PatternVersionSelector'
